/**
 * お知らせ一斉配信の配信先選択モーダルの画面固有ロジック。
 *
 * チェックの表示は選択済みID（groups_selected / members_selected）から算出する。
 * 共用モーダルの classAll は行ごとの checkall フラグを反転させる作りのため、
 * 施設をまたいで呼ぶと選択済みの施設が逆に解除されてしまう。
 */

/** ツリーの末端（クラス / 園児）が持つ配信先の実体 */
export type RelationLeafValue = {
  id: string | number;
  name: string;
  category_name?: string;
  kind?: string | number;
  service_id?: string | number;
};

/** 施設 > 学年 > クラス（園児）のツリーノード。末端は children を持たない */
export type RelationTreeNode = {
  id?: string | number;
  name?: string;
  children?: RelationTreeNode[];
  value?: RelationLeafValue;
};

export type RelationTreeInput = RelationTreeNode | RelationTreeNode[] | undefined | null;

const toNodes = (nodes: RelationTreeInput): RelationTreeNode[] => {
  if (!nodes) return [];
  return Array.isArray(nodes) ? nodes : [nodes];
};

/**
 * 選択済みIDを引き当て用の Set にする。テンプレートから行ごとに毎 digest 呼ばれるため、
 * 配列の線形探索のままだと重い（160施設・1920クラスで 19ms → 0.4ms）。
 * IDは数値・文字列が混ざるので、共用の inArray に揃えて文字列化する。
 */
const selectedSetCache = new WeakMap<object, Set<string>>();

const toSelectedSet = (selectedIds: ReadonlyArray<string | number>): Set<string> => {
  const key = selectedIds as unknown as object;
  const cached = selectedSetCache.get(key);
  if (cached) return cached;

  const set = new Set(selectedIds.map((id) => String(id)));
  selectedSetCache.set(key, set);
  return set;
};

const isSelectedIn = (id: string | number | undefined, selected: Set<string>): boolean => {
  if (id === undefined || id === null) return false;
  return selected.has(String(id));
};

/** ノード配下の、検索文字列に部分一致する末端（クラス / 園児）を集める */
export const collectVisibleLeaves = (nodes: RelationTreeInput, searchString: string): RelationTreeNode[] => {
  const leaves: RelationTreeNode[] = [];

  toNodes(nodes).forEach((node) => {
    if (!node) return;
    (node.children ?? []).forEach((child) => {
      if (child.children) {
        leaves.push(...collectVisibleLeaves(child, searchString));
      } else if (!searchString || (child.name ?? '').indexOf(searchString) !== -1) {
        leaves.push(child);
      }
    });
  });

  return leaves;
};

/** ヒット0件の施設・学年を出さないための判定 */
export const hasVisibleLeaves = (nodes: RelationTreeInput, searchString: string): boolean =>
  collectVisibleLeaves(nodes, searchString).length > 0;

/** 表示中の末端がすべて選択済みか。表示中が0件のときは false */
export const isAllLeavesSelected = (
  nodes: RelationTreeInput,
  searchString: string,
  selectedIds: ReadonlyArray<string | number>
): boolean => {
  const leaves = collectVisibleLeaves(nodes, searchString);
  if (!leaves.length) return false;
  const selected = toSelectedSet(selectedIds);
  return leaves.every((leaf) => isSelectedIn(leaf.id, selected));
};

/** 選択済みの配信先を持つ施設の数（クラス・園児の和集合） */
export const countSelectedServices = (
  groups: ReadonlyArray<RelationTreeNode> | undefined,
  members: ReadonlyArray<RelationTreeNode> | undefined,
  groupsSelected: ReadonlyArray<string | number>,
  membersSelected: ReadonlyArray<string | number>
): number => {
  const serviceIds = new Set<string>();
  const groupsSelectedSet = toSelectedSet(groupsSelected);
  const membersSelectedSet = toSelectedSet(membersSelected);

  (groups ?? []).forEach((service) => {
    const selected = collectVisibleLeaves(service, '').some((leaf) => isSelectedIn(leaf.id, groupsSelectedSet));
    if (selected) serviceIds.add(String(service.id));
  });

  (members ?? []).forEach((service) => {
    // 園児ツリーは在園・入園前・卒退園の3本に分かれている
    const nodes = [
      ...(service.children ?? []),
      ...((service as { before_admission_children?: RelationTreeNode[] }).before_admission_children ?? []),
      ...((service as { retire_children?: RelationTreeNode[] }).retire_children ?? []),
    ];
    const selected = collectVisibleLeaves(nodes, '').some((leaf) => isSelectedIn(leaf.id, membersSelectedSet));
    if (selected) serviceIds.add(String(service.id));
  });

  return serviceIds.size;
};

/**
 * ヘッダーに出す選択件数ラベル。
 * 例: 「選択中： 0 件」「選択中： 50 施設・128 クラス・園児 2 名」
 *
 * showServiceCount は施設が複数並ぶとき（統括アカウントの全体表示）だけ true。
 * memberLabel は学童では「児童」になるため translate の結果を受け取る。
 */
export const buildSelectionSummaryLabel = (
  serviceCount: number,
  groupCount: number,
  memberCount: number,
  showServiceCount: boolean,
  memberLabel: string
): string => {
  if (!groupCount && !memberCount) return '選択中： 0 件';

  const parts: string[] = [];
  if (showServiceCount) parts.push(`${serviceCount} 施設`);
  if (groupCount) parts.push(`${groupCount} クラス`);
  if (memberCount) parts.push(`${memberLabel} ${memberCount} 名`);

  return `選択中： ${parts.join('・')}`;
};
